import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, Trophy, Sparkles, Calendar, LogIn } from "lucide-react";
import { useAuth } from "../components/AuthProvider";

const API = process.env.REACT_APP_BACKEND_URL;

const FACTOR_LABELS = {
  date: "Date Night",
  creative: "Creative Collab",
  funny: "Comedic Chemistry",
  talented: "Raw Talent",
  leader: "Leadership",
  fight: "Fight Night",
  goat: "GOAT Debate",
  inspiring: "Inspiring",
  intelligent: "Intelligent",
  charismatic: "Charismatic",
  evening: "Unforgettable Evening",
};

export default function WeeklyWrapped() {
  const nav = useNavigate();
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    fetch(`${API}/api/wrapped/weekly`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setErr("Couldn't load your Weekly Wrapped."));
  }, [loading, user]);

  if (loading) {
    return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Loading…</div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center">
          <div className="font-display text-5xl leading-[0.9]">Weekly Wrapped</div>
          <p className="mt-3 text-sm text-[#FDFBF7]/70">Sign in to see how your week stacked up.</p>
          <button data-testid="ww-signin-btn" onClick={login}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide">
            <LogIn size={16} /> Sign in with Google
          </button>
        </div>
      </div>
    );
  }
  if (err) return <div className="min-h-screen pt-16 text-center text-[#FF107A]">{err}</div>;
  if (!data) return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Wrapping your week…</div>;

  if (data.empty) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center" data-testid="ww-empty">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-[#FFD700]/15 border border-[#FFD700]/40 items-center justify-center mb-6">
            <Calendar size={26} className="text-[#FFD700]" />
          </div>
          <div className="font-display text-4xl">Nothing to wrap yet.</div>
          <p className="mt-3 text-sm text-[#FDFBF7]/70">{data.message}</p>
          <button data-testid="ww-play-btn" onClick={() => nav("/daily")}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF107A] text-[#FDFBF7] px-6 py-3 font-bold glow-pink">
            <Flame size={16} /> Play today's Smash
          </button>
        </div>
      </div>
    );
  }

  const factorKey = data.top_factor?.factor_id;
  const factorLabel = FACTOR_LABELS[factorKey] || (factorKey || "—").replace("_", " ");

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="ww-back-btn" onClick={() => nav("/home")}
          className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Home
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          data-testid="ww-hero"
          className="mt-6 rounded-3xl border border-[#FFD700]/40 p-6 relative overflow-hidden"
          style={{ background: "radial-gradient(circle at 20% 15%, rgba(255,215,0,0.25), transparent 60%), #16141D" }}
        >
          <div className="absolute inset-0 halftone opacity-15 pointer-events-none" />
          <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700] relative">
            <Sparkles size={11} className="inline mr-1" /> Your Weekly Wrapped
          </div>
          <div className="mt-2 font-display text-4xl leading-[0.9]" data-testid="ww-total-score">
            {data.total_score} pts
          </div>
          <div className="mt-1 text-sm text-[#FDFBF7]/70">
            {data.plays} plays · #{data.weekly_rank} globally
          </div>
        </motion.div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <StatCard label="Best Day" value={data.best_day_score} testId="ww-best-day" accent="#FF107A" />
          <StatCard label="Streak" value={`${data.daily_streak}d`} testId="ww-streak" accent="#00E5FF" />
          <StatCard label="Perfect" value={data.perfect_games} testId="ww-perfect" accent="#FFD700" />
        </div>

        <div className="mt-4 rounded-2xl border border-[#A855F7]/40 bg-[#A855F7]/10 p-4" data-testid="ww-top-factor">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A855F7]">Your top Smash Factor</div>
          <div className="mt-1 font-display text-2xl text-[#FDFBF7] capitalize">{factorLabel}</div>
          {data.top_factor && (
            <div className="text-xs text-[#FDFBF7]/70 mt-0.5">
              {data.top_factor.score} pts across {data.top_factor.plays} play{data.top_factor.plays === 1 ? "" : "s"}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FDFBF7]/60">Week window</div>
          <div className="mt-1 text-sm text-[#FDFBF7]">
            {data.week_start} → {data.week_end}
          </div>
          <div className="text-xs text-[#FDFBF7]/60 mt-1">
            {data.perfect_rounds} perfect rounds · CSS {data.css} · {data.title}
          </div>
        </div>

        <button data-testid="ww-play-more" onClick={() => nav("/daily")}
          className="mt-6 w-full rounded-full bg-[#FF107A] text-[#FDFBF7] py-3 font-bold glow-pink flex items-center justify-center gap-2">
          <Flame size={16} /> Play today's Smash
        </button>
        <button data-testid="ww-leaderboard" onClick={() => nav("/leaderboards/daily")}
          className="mt-3 w-full rounded-full border border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold flex items-center justify-center gap-2">
          <Trophy size={16} /> See the weekly leaderboard
        </button>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, testId, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 px-3 py-3 text-center">
    <div className="font-display text-2xl leading-none tabular" style={{ color: accent }} data-testid={testId}>{value}</div>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-1.5">{label}</div>
  </div>
);
